import type { List as ImmutableList } from 'immutable'
import { Map as ImmutableMap } from 'immutable'
import moment from 'moment'

import { timeFormat } from 'utils/timeFormat'
import type { ProjectedDelivery, RecipeItem, UserOrder } from 'utils/types/order'

export const filterOrders = (orders: ImmutableList<UserOrder>) =>
  orders.filter((order) => {
    const phase = order?.get('phase')

    return phase !== 'delivered'
  })

export const getOrderState = (
  state: string,
  deliveryDate?: string,
  recipeItems?: ImmutableList<RecipeItem>,
  phase?: string,
  cancellable?: boolean,
) => {
  const isDeliveryDay = moment().isSame(deliveryDate, 'day')

  if (phase === 'pre_menu') {
    return 'scheduled'
  }

  if (state === 'committed' && isDeliveryDay) {
    return 'dispatched'
  }

  if ((state === 'committed' && !isDeliveryDay) || (state === 'pending' && !cancellable)) {
    return 'confirmed'
  }

  if (state === 'pending' && !recipeItems?.size) {
    return 'menu open'
  }

  if (state === 'pending' && !!recipeItems?.size) {
    return 'recipes chosen'
  }

  return state
}

export const getDeliveryDayRescheduledReason = (unavailableReason?: string) => {
  if (unavailableReason) {
    if (unavailableReason === 'holiday') {
      return "We've had to change your regular delivery day due to the bank holiday."
    } else {
      return 'Choose recipes now.'
    }
  }

  return undefined
}

type PendingOrderSummary = ImmutableMap<string, any>

export const transformPendingOrders = (orders: ImmutableList<UserOrder>) => {
  const futureOrders = filterOrders(orders)

  return futureOrders.reduce<ImmutableMap<string, PendingOrderSummary>>((acc, order) => {
    const id = order?.get('id')
    const state = order?.get('state') || ''
    const phase = order?.get('phase')
    const whenLive = order?.get('whenLive')
    const shouldCutoffAt = order?.get('shouldCutoffAt')
    const deliveryDayId = order?.get('deliveryDayId')
    const deliveryDate = order?.get('deliveryDate') || ''
    const humanDeliveryDay = order?.get('humanDeliveryDate')
    const deliverySlotId = order?.get('deliverySlotId')
    const deliverySlot = order?.get('deliverySlot')
    const prices = order?.get('prices')
    const recipeItems = order?.get('recipeItems')
    const productItems = order?.get('productItems')
    const box = order?.get('box')
    const originalDeliveryDay = order?.getIn(['originalDeliveryDay', 'humanDate'], null)
    const unavailableReason = order?.getIn(['originalDeliveryDay', 'unavailableReason'], '')
    const period = order?.get('period')
    const shippingAddress = order?.get('shippingAddress')
    const number = order?.get('number')
    const subscriptionId = order?.get('subscriptionId')
    const isTestOrder = order?.get('isTestOrder')
    const orderType = order?.get('orderType')

    const restorable = state === 'cancelled' && typeof subscriptionId === 'string'
    const cancellable = (phase === 'awaiting_choices' || phase === 'open') && !restorable
    const orderState = getOrderState(state, deliveryDate, recipeItems, phase, cancellable)
    const deliveryDayRescheduledReason = getDeliveryDayRescheduledReason(unavailableReason)

    const summary: PendingOrderSummary = ImmutableMap({
      id,
      isTestOrder,
      orderState,
      orderType,
      whenMenuOpen: whenLive,
      shouldCutoffAt,
      shippingAddress,
      shippingAddressId: shippingAddress?.get('id'),
      coreDeliveryDayId: deliveryDayId,
      deliveryDay: deliveryDate,
      humanDeliveryDay,
      originalDeliveryDay,
      deliveryDayRescheduledReason,
      deliverySlotId,
      deliverySlotStart: deliverySlot?.get('deliveryStart'),
      deliverySlotEnd: deliverySlot?.get('deliveryEnd'),
      cancellable,
      restorable,
      phase,
      priceBreakdown: ImmutableMap({
        grossRecipesPrice: Number(prices?.get('recipeTotal')),
        grossExtrasPrice: Number(prices?.get('productTotal')),
        grossShippingPrice: Number(prices?.get('deliveryTotal')),
        grossOrderPrice: Number(prices?.get('grossTotal')),
        flatDiscountAmount: Number(prices?.get('totalDiscount')),
        percentageDiscountAmount: Number(prices?.get('percentageOff')),
        netOrderPrice: Number(prices?.get('total')),
        surcharge: Number(prices?.get('surchargeTotal')),
      }),
      recipes: recipeItems?.map((item) =>
        ImmutableMap({
          id: item?.get('id'),
          title: item?.get('title'),
          recipeId: item?.get('recipeId'),
          recipeUuid: item?.get('recipeUuid'),
          image: item?.getIn(['media', 0, 'urls', 1, 'src'], ''),
          quantity: item?.get('quantity'),
        }),
      ),
      products: ImmutableMap({
        total: productItems?.size,
        elements: productItems?.map((item) => {
          const listPrice = Number(item?.get('listPrice', '0'))
          const quantity = Number(item?.get('quantity', '0'))

          return ImmutableMap({
            id: item?.get('id', ''),
            unitPrice: quantity > 0 ? listPrice / quantity : 0,
            quantity: item?.get('quantity'),
            title: item?.get('title', ''),
            image: item?.getIn(['media', 0, 'urls', 1, 'src'], ''),
          })
        }),
      }),
      portionsCount: box?.get('numPortions'),
      periodId: period?.get('id'),
      availableFrom: period?.get('whenStart'),
      availableTo: period?.get('whenCutoff'),
      number,
    })

    return (acc || ImmutableMap<string, PendingOrderSummary>()).set(id as string, summary)
  }, ImmutableMap<string, PendingOrderSummary>())
}

type ProjectedOrderSummary = ImmutableMap<
  | 'id'
  | 'orderState'
  | 'deliveryDay'
  | 'humanDeliveryDay'
  | 'whenMenuOpen'
  | 'isProjected'
  | 'restorable'
  | 'cancellable'
  | 'deliveryDayId',
  string | boolean
>

type ProjectedOrdersByDate = ImmutableMap<string, ProjectedOrderSummary>

export const transformProjectedDeliveries = (
  projectedDeliveries: ImmutableList<ProjectedDelivery>,
): ProjectedOrdersByDate => {
  const initial: ProjectedOrdersByDate = ImmutableMap<string, ProjectedOrderSummary>()

  const reducer = (
    acc?: ProjectedOrdersByDate,
    delivery?: ProjectedDelivery,
  ): ProjectedOrdersByDate => {
    if (!delivery) return acc as ProjectedOrdersByDate

    const date = delivery.get('deliveryDate', '')
    const whenMenuLive = delivery.get('menuOpenDate', '')
    const skipped = delivery.get('skipped', false)
    const humanDeliveryDay = timeFormat(date, 'day')

    const orderState = skipped ? 'cancelled' : 'scheduled'

    const summary = ImmutableMap({
      id: date,
      orderState,
      deliveryDay: date,
      humanDeliveryDay,
      whenMenuOpen: whenMenuLive,
      isProjected: true,
      restorable: skipped,
      cancellable: true,
      deliveryDayId: date,
    }) as ProjectedOrderSummary

    return (acc ?? initial).set(date, summary)
  }

  return projectedDeliveries.reduce<ProjectedOrdersByDate>(reducer, initial)
}
